import React from "react";
import RouteRiskBreakdown, {
  RiskBreakdownResponse,
  RiskRecommendationsResponse,
} from "./RouteRiskBreakdown";
import RouteTariffBreakdown from "./RouteTariffBreakdown";
import {
  buildSelectedScenarioPanelViewModel,
  getComputedTotalCostValue,
  getExtendedProductCostValue,
  getShipmentCostValue,
  getTariffCostValue,
  type LocationNode,
  shouldDisplayRiskInsights,
  WL_RED,
} from "./routeAnalyzerShared";

type SelectedScenarioPanelProps = {
  panelRoute: any;
  panelRouteIdx: number;
  allNodeLookup: Map<string, LocationNode>;
  optimization: string;
  orderDate: string;
  visibleRoutes: any[];
  onOrderDateChange: (value: string) => void;
  onClose: () => void;
  expandedRiskScore: boolean;
  onToggleExpandedRiskScore: () => void;
  panelRiskBreakdown: RiskBreakdownResponse | null;
  panelRiskBreakdownLoading: boolean;
  panelRecommendations: RiskRecommendationsResponse | null;
  panelRecommendationsLoading: boolean;
  showRiskGraph: boolean;
  onToggleRiskView: () => void;
  expandedTariffBreakdown: boolean;
  onToggleExpandedTariffBreakdown: () => void;
};

const SelectedScenarioPanel: React.FC<SelectedScenarioPanelProps> = ({
  panelRoute,
  panelRouteIdx,
  allNodeLookup,
  optimization,
  orderDate,
  visibleRoutes,
  onOrderDateChange,
  onClose,
  expandedRiskScore,
  onToggleExpandedRiskScore,
  panelRiskBreakdown,
  panelRiskBreakdownLoading,
  panelRecommendations,
  panelRecommendationsLoading,
  showRiskGraph,
  onToggleRiskView,
  expandedTariffBreakdown,
  onToggleExpandedTariffBreakdown,
}) => {
  const viewModel = buildSelectedScenarioPanelViewModel({
    panelRoute,
    panelRouteIdx,
    allNodeLookup,
    optimization,
    orderDate,
    visibleRoutes,
  });
  const orderDateInputId = `selected-scenario-order-date-${panelRoute.route_id ?? panelRouteIdx}`;
  const delayPct = panelRiskBreakdown?.delay_probability_pct ?? viewModel.riskPct;
  let delayColor = "#EF2D3E";
  if (delayPct < 20) {
    delayColor = "#16a34a";
  } else if (delayPct < 40) {
    delayColor = "#F59E0B";
  }
  const topFeatures = panelRiskBreakdown?.top_features ?? [];
  const recommendationsRaw = panelRecommendations?.recommendations;
  const recommendations: Array<{ title?: string; why?: string; actions?: string[] }> =
    Array.isArray(recommendationsRaw) ? recommendationsRaw : [];
  const showRiskInsightSection =
    panelRoute != null && shouldDisplayRiskInsights(panelRoute);

  return (
    <React.Fragment key="panel">
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.2)",
          zIndex: 1200,
          border: "none",
          padding: 0,
          margin: 0,
          cursor: "pointer",
        }}
      />

      <div
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: 500,
          maxWidth: "100vw",
          backgroundColor: "#fff",
          boxShadow: "-4px 0 30px rgba(0,0,0,0.15)",
          zIndex: 1300,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            borderBottom: "1px solid #EAEAEA",
            padding: "24px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 10,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 600, color: "#000", margin: 0 }}>
            Selected Scenario
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 8,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6B7280",
            }}
            aria-label="Close panel"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 500, color: "#6B7280", margin: "0 0 6px 0" }}>Scenario Name</p>
            <p style={{ fontSize: 14, color: "#111827", margin: 0 }}>
              {viewModel.routePath} {"\u2022"} {optimization.toLowerCase()} {"\u2022"} Q{panelRouteIdx + 1}
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "#000", margin: "0 0 12px 0" }}>
              Why This Route Was Recommended
            </h3>
            <div
              style={{
                backgroundColor: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <p style={{ fontSize: 14, color: "#1E3A8A", lineHeight: 1.625, margin: 0 }}>
                {viewModel.whyRecommended}
              </p>
            </div>
          </div>

          <div style={{ backgroundColor: "#F9FAFB", borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "#000", margin: "0 0 16px 0" }}>
              Route Details
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {([
                { label: "Mode", value: viewModel.modeLabel, valueColor: "#000" },
                { label: "Shipment Cost", value: viewModel.shipmentCostDisplay, valueColor: "#000" },
                { label: "Product Cost", value: viewModel.productCostDisplay, valueColor: "#000" },
                ...(viewModel.tariffCostDisplay
                  ? [{ label: "Tariff Cost", value: viewModel.tariffCostDisplay, valueColor: "#EF2D3E" }]
                  : []),
                { label: "Total Cost", value: viewModel.totalCostDisplay, valueColor: "#000" },
                { label: "Risk Score", value: `${viewModel.riskPct}%`, valueColor: viewModel.riskColor },
                { label: "Distance", value: viewModel.distanceDisplay, valueColor: "#000" },
              ] as { label: string; value: string; valueColor: string }[]).map(({ label, value, valueColor }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 14, color: "#6B7280" }}>{label}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: valueColor }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor={orderDateInputId}
              style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 8 }}
            >
              Order Date <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              id={orderDateInputId}
              type="date"
              value={orderDate}
              onChange={(e) => onOrderDateChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #EAEAEA",
                borderRadius: 8,
                fontSize: 14,
                color: "#111827",
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
              onFocus={(e) => {
                e.currentTarget.style.border = `1px solid ${WL_RED}`;
                e.currentTarget.style.boxShadow = "0 0 0 2px #EF2D3E, 0 0 0 5px rgba(239,45,62,0.12)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #EAEAEA";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            {orderDate && viewModel.transitDays != null && (
              <div
                style={{
                  marginTop: 12,
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      backgroundColor: "#DBEAFE",
                      fontSize: 13,
                      lineHeight: 1,
                    }}
                  >
                    {"\uD83D\uDCC5"}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#1E3A8A",
                    }}
                  >
                    {(() => {
                      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(orderDate);
                      const d = match
                        ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
                        : new Date(orderDate);
                      if (Number.isNaN(d.getTime())) return `If Scheduled on ${orderDate}`;
                      const today = new Date();
                      const isToday =
                        d.getFullYear() === today.getFullYear() &&
                        d.getMonth() === today.getMonth() &&
                        d.getDate() === today.getDate();
                      if (isToday) return "If Scheduled Today";
                      const formatted = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                      return `If Scheduled on ${formatted}`;
                    })()}
                  </span>
                </div>
                <p style={{ fontSize: 22, fontWeight: 700, color: "#1E3A8A", margin: "0 0 4px 0", lineHeight: 1.2 }}>
                  Delivers in {viewModel.transitDays} days
                </p>
                <p style={{ fontSize: 12, color: "#1D4ED8", margin: 0 }}>
                  {viewModel.estimatedDeliveryRange
                    ? `Estimated delivery window: ${viewModel.estimatedDeliveryRange}`
                    : `Estimated delivery: ${viewModel.estimatedArrival}`}
                </p>
              </div>
            )}
          </div>

          {showRiskInsightSection && (
            <RouteRiskBreakdown
              expanded={expandedRiskScore}
              onToggleExpanded={onToggleExpandedRiskScore}
              delayPct={delayPct}
              delayColor={delayColor}
              showRiskGraph={showRiskGraph}
              onToggleView={onToggleRiskView}
              panelRiskBreakdownLoading={panelRiskBreakdownLoading}
              panelRecommendations={panelRecommendations}
              panelRecommendationsLoading={panelRecommendationsLoading}
              topFeatures={topFeatures}
              recommendations={recommendations}
            />
          )}

          <RouteTariffBreakdown
            expanded={expandedTariffBreakdown}
            onToggleExpanded={onToggleExpandedTariffBreakdown}
            routeTariffCostPresent={panelRoute?.tariff_cost != null}
            routeProductCost={getExtendedProductCostValue(panelRoute)}
            routeShipmentCost={getShipmentCostValue(panelRoute)}
            routeTariffCost={getTariffCostValue(panelRoute)}
            routeTotalCost={getComputedTotalCostValue(panelRoute)}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default SelectedScenarioPanel;
